import React, { useEffect, useState, createContext, useContext } from 'react';
import { User as SupabaseUser } from '@supabase/supabase-js';
import { supabase } from '../utils/supabase';
import { User, UserRole, ModulePermissions, getRolePermissions } from '../types';
interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (email: string, password: string) => Promise<{
    success: boolean;
    error?: string;
  }>;
  register: (name: string, email: string, password: string, role?: UserRole) => Promise<{
    success: boolean;
    error?: string;
  }>;
  logout: () => void;
  permissions: ModulePermissions | null;
  updateUserPreferences: (language: string, timezone: string) => void;
  updateUser: (updates: Partial<User>) => void;
  users: User[];
  updateUsers: (users: User[]) => void;
}
const AuthContext = createContext<AuthContextType | undefined>(undefined);
// Mock users for demo (fallback si Supabase no está configurado)
const MOCK_USERS: User[] = [{
  id: '1',
  email: 'admin@savidna.com',
  password: 'admin123',
  name: 'Administrador Sistema',
  role: 'administrador',
  language: 'es',
  timezone: 'America/Santo_Domingo',
  createdAt: new Date()
}, {
  id: '2',
  email: 'doctor@savidna.com',
  password: 'doctor123',
  name: 'Dr. Carlos Méndez',
  role: 'doctor',
  language: 'es',
  timezone: 'America/Santo_Domingo',
  createdAt: new Date()
}, {
  id: '3',
  email: 'recepcion@savidna.com',
  password: 'recepcion123',
  name: 'María García',
  role: 'recepcionista',
  language: 'es',
  timezone: 'America/Santo_Domingo',
  createdAt: new Date()
}, {
  id: '4',
  email: 'callcenter@savidna.com',
  password: 'callcenter123',
  name: 'Juan Rodríguez',
  role: 'callcenter',
  language: 'es',
  timezone: 'America/Santo_Domingo',
  createdAt: new Date()
}];
// Helper function to get users from localStorage
const getUsersFromStorage = (): User[] => {
  const saved = localStorage.getItem('savidna-users');
  if (saved) {
    try {
      const parsed = JSON.parse(saved);
      return parsed.map((u: any) => ({
        ...u,
        createdAt: new Date(u.createdAt)
      }));
    } catch (e) {
      console.error('Error parsing users from localStorage:', e);
      return MOCK_USERS;
    }
  }
  // Initialize with mock users if nothing in storage
  localStorage.setItem('savidna-users', JSON.stringify(MOCK_USERS));
  return MOCK_USERS;
};
interface AuthProviderProps {
  children: React.ReactNode;
}
export function AuthProvider({
  children
}: AuthProviderProps) {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [users, setUsers] = useState<User[]>(getUsersFromStorage);
  const [useSupabase, setUseSupabase] = useState(false);
  // Check if Supabase is configured
  useEffect(() => {
    const checkSupabase = async () => {
      try {
        const {
          data,
          error
        } = await supabase.from('users').select('count').limit(1);
        if (!error) {
          // Verificar si hay usuarios en Supabase
          const {
            count
          } = await supabase.from('users').select('*', {
            count: 'exact',
            head: true
          });
          if (count && count > 0) {
            setUseSupabase(true);
            console.log('✅ Supabase conectado con usuarios registrados');
          } else {
            setUseSupabase(false);
            console.log('⚠️ Supabase conectado pero sin usuarios. Usando modo demo con localStorage.');
            console.log('💡 Para usar Supabase: Registra usuarios desde la página de registro o ejecuta el script SQL de usuarios.');
          }
        }
      } catch (e) {
        console.log('ℹ️ Usando modo demo (localStorage). Los usuarios demo están disponibles.');
        setUseSupabase(false);
      }
    };
    checkSupabase();
  }, []);
  useEffect(() => {
    const loadUser = async () => {
      if (useSupabase) {
        // Load from Supabase
        try {
          const {
            data: {
              session
            }
          } = await supabase.auth.getSession();
          if (session?.user) {
            const {
              data: profile,
              error
            } = await supabase.from('users').select('*').eq('id', session.user.id).single();
            if (profile && !error) {
              setUser({
                id: profile.id,
                email: profile.email,
                password: '',
                name: profile.name,
                role: profile.role as UserRole,
                avatar: profile.avatar,
                language: profile.language,
                timezone: profile.timezone,
                createdAt: new Date(profile.created_at)
              });
            }
          }
        } catch (error) {
          console.error('Error loading user from Supabase:', error);
        }
      } else {
        // Load from localStorage
        const savedUser = localStorage.getItem('savidna-user');
        if (savedUser) {
          try {
            const parsed = JSON.parse(savedUser);
            setUser({
              ...parsed,
              createdAt: new Date(parsed.createdAt)
            });
          } catch (e) {
            console.error('Error parsing saved user:', e);
          }
        }
      }
      setIsLoading(false);
    };
    loadUser();
    if (useSupabase) {
      // Listen for auth changes
      const {
        data: {
          subscription
        }
      } = supabase.auth.onAuthStateChange(async (event, session) => {
        if (event === 'SIGNED_IN' && session?.user) {
          const {
            data: profile
          } = await supabase.from('users').select('*').eq('id', session.user.id).single();
          if (profile) {
            setUser({
              id: profile.id,
              email: profile.email,
              password: '',
              name: profile.name,
              role: profile.role as UserRole,
              avatar: profile.avatar,
              language: profile.language,
              timezone: profile.timezone,
              createdAt: new Date(profile.created_at)
            });
          }
        } else if (event === 'SIGNED_OUT') {
          setUser(null);
        }
      });
      return () => {
        subscription.unsubscribe();
      };
    }
  }, [useSupabase]);
  // Load all users (for admin)
  useEffect(() => {
    const loadUsers = async () => {
      if (user?.role === 'administrador') {
        if (useSupabase) {
          const {
            data,
            error
          } = await supabase.from('users').select('*').order('created_at', {
            ascending: false
          });
          if (data && !error) {
            setUsers(data.map(u => ({
              id: u.id,
              email: u.email,
              password: '',
              name: u.name,
              role: u.role as UserRole,
              avatar: u.avatar,
              language: u.language,
              timezone: u.timezone,
              createdAt: new Date(u.created_at)
            })));
          }
        } else {
          setUsers(getUsersFromStorage());
        }
      }
    };
    if (user) {
      loadUsers();
    }
  }, [user, useSupabase]);
  useEffect(() => {
    if (!useSupabase) {
      localStorage.setItem('savidna-users', JSON.stringify(users));
    }
  }, [users, useSupabase]);
  const login = async (email: string, password: string): Promise<{
    success: boolean;
    error?: string;
  }> => {
    if (useSupabase) {
      // Supabase login
      try {
        const {
          data,
          error
        } = await supabase.auth.signInWithPassword({
          email,
          password
        });
        if (error) {
          return {
            success: false,
            error: error.message === 'Invalid login credentials' ? 'Credenciales inválidas' : error.message
          };
        }
        if (data.user) {
          const {
            data: profile,
            error: profileError
          } = await supabase.from('users').select('*').eq('id', data.user.id).single();
          if (profile && !profileError) {
            setUser({
              id: profile.id,
              email: profile.email,
              password: '',
              name: profile.name,
              role: profile.role as UserRole,
              avatar: profile.avatar,
              language: profile.language,
              timezone: profile.timezone,
              createdAt: new Date(profile.created_at)
            });
          }
          return {
            success: true
          };
        }
        return {
          success: false,
          error: 'Error al iniciar sesión'
        };
      } catch (error: any) {
        return {
          success: false,
          error: error.message || 'Error al iniciar sesión'
        };
      }
    } else {
      // localStorage login con usuarios demo
      await new Promise(resolve => setTimeout(resolve, 500));
      const currentUsers = getUsersFromStorage();
      const foundUser = currentUsers.find(u => u.email.toLowerCase() === email.toLowerCase());
      if (!foundUser) {
        return {
          success: false,
          error: 'Usuario no encontrado'
        };
      }
      if (foundUser.password !== password) {
        return {
          success: false,
          error: 'Contraseña incorrecta'
        };
      }
      setUser(foundUser);
      localStorage.setItem('savidna-user', JSON.stringify(foundUser));
      setUsers(currentUsers);
      return {
        success: true
      };
    }
  };
  const register = async (name: string, email: string, password: string, role: UserRole = 'recepcionista'): Promise<{
    success: boolean;
    error?: string;
  }> => {
    if (useSupabase) {
      // Supabase register
      try {
        const {
          data: authData,
          error: authError
        } = await supabase.auth.signUp({
          email,
          password
        });
        if (authError) {
          return {
            success: false,
            error: authError.message === 'User already registered' ? 'El correo electrónico ya está registrado' : authError.message
          };
        }
        if (authData.user) {
          const {
            error: profileError
          } = await supabase.from('users').insert({
            id: authData.user.id,
            email,
            name,
            role,
            language: 'es',
            timezone: 'America/Santo_Domingo'
          });
          if (profileError) {
            return {
              success: false,
              error: 'Error al crear perfil de usuario'
            };
          }
          setUser({
            id: authData.user.id,
            email,
            password: '',
            name,
            role,
            language: 'es',
            timezone: 'America/Santo_Domingo',
            createdAt: new Date()
          });
          return {
            success: true
          };
        }
        return {
          success: false,
          error: 'Error al registrar usuario'
        };
      } catch (error: any) {
        return {
          success: false,
          error: error.message || 'Error al registrar usuario'
        };
      }
    } else {
      // localStorage register
      await new Promise(resolve => setTimeout(resolve, 500));
      const currentUsers = getUsersFromStorage();
      const existingUser = currentUsers.find(u => u.email.toLowerCase() === email.toLowerCase());
      if (existingUser) {
        return {
          success: false,
          error: 'El correo electrónico ya está registrado'
        };
      }
      const newUser: User = {
        id: crypto.randomUUID(),
        email,
        password,
        name,
        role,
        language: 'es',
        timezone: 'America/Santo_Domingo',
        createdAt: new Date()
      };
      const updatedUsers = [...currentUsers, newUser];
      setUsers(updatedUsers);
      localStorage.setItem('savidna-users', JSON.stringify(updatedUsers));
      setUser(newUser);
      localStorage.setItem('savidna-user', JSON.stringify(newUser));
      return {
        success: true
      };
    }
  };
  const logout = async () => {
    if (useSupabase) {
      await supabase.auth.signOut();
    } else {
      localStorage.removeItem('savidna-user');
    }
    setUser(null);
  };
  const updateUserPreferences = async (language: string, timezone: string) => {
    if (user) {
      if (useSupabase) {
        const {
          error
        } = await supabase.from('users').update({
          language,
          timezone
        }).eq('id', user.id);
        if (!error) {
          const updatedUser = {
            ...user,
            language,
            timezone
          };
          setUser(updatedUser);
        }
      } else {
        const updatedUser = {
          ...user,
          language,
          timezone
        };
        setUser(updatedUser);
        localStorage.setItem('savidna-user', JSON.stringify(updatedUser));
        setUsers(prev => prev.map(u => u.id === user.id ? updatedUser : u));
      }
    }
  };
  const updateUser = async (updates: Partial<User>) => {
    if (user) {
      if (useSupabase) {
        const {
          error
        } = await supabase.from('users').update({
          name: updates.name,
          role: updates.role,
          avatar: updates.avatar,
          language: updates.language,
          timezone: updates.timezone
        }).eq('id', user.id);
        if (!error) {
          const updatedUser = {
            ...user,
            ...updates
          };
          setUser(updatedUser);
        }
      } else {
        const updatedUser = {
          ...user,
          ...updates
        };
        setUser(updatedUser);
        localStorage.setItem('savidna-user', JSON.stringify(updatedUser));
        setUsers(prev => prev.map(u => u.id === user.id ? updatedUser : u));
      }
    }
  };
  const updateUsers = async (updatedUsers: User[]) => {
    if (useSupabase) {
      for (const updatedUser of updatedUsers) {
        await supabase.from('users').update({
          name: updatedUser.name,
          role: updatedUser.role,
          avatar: updatedUser.avatar,
          language: updatedUser.language,
          timezone: updatedUser.timezone
        }).eq('id', updatedUser.id);
      }
    } else {
      localStorage.setItem('savidna-users', JSON.stringify(updatedUsers));
    }
    setUsers(updatedUsers);
    if (user) {
      const updatedCurrentUser = updatedUsers.find(u => u.id === user.id);
      if (updatedCurrentUser) {
        setUser(updatedCurrentUser);
        if (!useSupabase) {
          localStorage.setItem('savidna-user', JSON.stringify(updatedCurrentUser));
        }
      }
    }
  };
  const permissions = user ? getRolePermissions(user.role) : null;
  return <AuthContext.Provider value={{
    user,
    isAuthenticated: !!user,
    isLoading,
    login,
    register,
    logout,
    permissions,
    updateUserPreferences,
    updateUser,
    users,
    updateUsers
  }} data-id="element-1792">
      {children}
    </AuthContext.Provider>;
}
export function useAuth(): AuthContextType {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}